/**
 * Echelons — era progression (I–V). Advancing costs resources and requires
 * the data `requirements`: buildings, military buildings, techs, minimum
 * population, minimum production, and strategic points.
 *
 * PROTOTYPE MAPPING: strategic_points are earned by destroying enemy
 * formations in battle. Population counts soldiers + workers.
 */
import type { GameDB } from "../data/gamedb";
import type { EchelonDef } from "../data/types";
import type { Economy } from "./economy";
import type { ResearchState } from "./research";

export interface EchelonState {
  /** 1-based echelon index. */
  index: number;
}

export function createEchelon(): EchelonState {
  return { index: 1 };
}

export interface AdvanceStats {
  population: number;
  /** Current per-second production by resource id. */
  production: Record<string, number>;
  strategicPoints: number;
}

export function echelonDef(db: GameDB, index: number): EchelonDef | undefined {
  for (const e of db.echelons.values()) {
    if (e.index === index) return e;
  }
  return undefined;
}

export function advanceCost(db: GameDB, state: EchelonState): Record<string, number> {
  const next = echelonDef(db, state.index + 1);
  return next?.advance_cost ?? {};
}

/** Check the next echelon's requirements; returns human-readable gaps. */
export function canAdvance(
  db: GameDB,
  state: EchelonState,
  research: ResearchState,
  activeBuildingIds: Set<string>,
  activeMilitaryIds: Set<string>,
  stats: AdvanceStats,
): { ok: boolean; missing: string[] } {
  const next = echelonDef(db, state.index + 1);
  if (!next) return { ok: false, missing: ["no further echelon"] };
  return checkRequirements(next, research, activeBuildingIds, activeMilitaryIds, stats);
}

function checkRequirements(
  next: EchelonDef,
  research: ResearchState,
  activeBuildingIds: Set<string>,
  activeMilitaryIds: Set<string>,
  stats: AdvanceStats,
): { ok: boolean; missing: string[] } {
  const req = next.requirements;
  const missing: string[] = [];
  for (const b of req.buildings ?? []) {
    if (!activeBuildingIds.has(b)) missing.push(`building: ${b}`);
  }
  for (const b of req.military_buildings ?? []) {
    if (!activeMilitaryIds.has(b)) missing.push(`military: ${b}`);
  }
  for (const t of req.techs ?? []) {
    if (!research.completed.has(t)) missing.push(`tech: ${t}`);
  }
  if (stats.population < (req.min_population ?? 0)) {
    missing.push(`population ${stats.population}/${req.min_population}`);
  }
  const minProd = (req["min_production"] as Record<string, number>) ?? {};
  for (const [id, rate] of Object.entries(minProd)) {
    if ((stats.production[id] ?? 0) < rate) {
      missing.push(`${id} production ${(stats.production[id] ?? 0).toFixed(2)}/s (need ${rate})`);
    }
  }
  if (stats.strategicPoints < (req.strategic_points ?? 0)) {
    missing.push(`strategic points ${stats.strategicPoints}/${req.strategic_points} (earn in battle)`);
  }
  return { ok: missing.length === 0, missing };
}

export function doAdvance(
  db: GameDB,
  state: EchelonState,
  economy: Economy,
): boolean {
  if (!economy.pay(advanceCost(db, state))) return false;
  state.index += 1;
  return true;
}
